import { useState, useEffect, JSX } from "react";
import { TShiftSchema } from "../Models";
import { UseFormGetValues, UseFormSetValue, UseFormWatch } from "react-hook-form";
import { Button } from "@/components/ui/button";
import { useTranslation } from "react-i18next";
import { TFunction } from "i18next";
import { daysOfTheWeek } from "@/constants/days";
import { cn } from "@/lib/utils";

interface Props {
  watch: UseFormWatch<TShiftSchema>;
  getValues: UseFormGetValues<TShiftSchema>;
  setValue: UseFormSetValue<TShiftSchema>;
  holidayPayEnabled: boolean;
}

const WeekdayButtons = (props: Props): JSX.Element => {

  const { watch, getValues, setValue, holidayPayEnabled } = props;

  // Hooks
  const { t } = useTranslation(["common", "translation"]);

  // States
  const [_, setActiveDays] = useState<string[]>();
  const [allSelected, setAllSelected] = useState<boolean>(false);

  const handleSelectAllClick = () => {
    if(holidayPayEnabled) {
      daysOfTheWeek.forEach(day => day !== "holiday" && setValue(`${day}` as keyof TShiftSchema, !allSelected));
    }
    else {
      daysOfTheWeek.forEach(day => setValue(`${day}` as keyof TShiftSchema, !allSelected));
    }
    setAllSelected(!allSelected);
  };

  useEffect(() => {
    // Update active days when form values change
    const subscription = watch((values: any) => {
      const activeDays = daysOfTheWeek.filter(day => values[`${day}`]);
      setActiveDays(activeDays);
      setAllSelected(activeDays.length === daysOfTheWeek.length);
    });

    return () => subscription.unsubscribe();
  }, []);

  const toggleDay = (day: string) => {
    // If trying to toggle holiday while holiday pay is enabled, prevent the toggle
    if (day === "holiday" && holidayPayEnabled) {
      return;
    }

    const currentDayValue = getValues(`${day}` as keyof TShiftSchema);
    setValue(`${day}` as keyof TShiftSchema, !currentDayValue);

    // If enabling holiday, disable holiday pay
    if (day === "holiday" && !currentDayValue) {
      setValue("applyHolidayPay", false);
    }
  };

  return (
    <div className="flex-wrap-2">
      {daysOfTheWeek.map((day, index) => (
        <DayButton
          t={ t }
          key={day}
          day={day}
          tabIndex={index + 5}
          isActive={getValues(`${day}` as keyof TShiftSchema) as boolean}
          onClick={() => toggleDay(day)}
          disabled={day === "holiday" && holidayPayEnabled}
        />
      ))}
      <Button
        variant="ghost"
        type="button"
        onClick={handleSelectAllClick}
      >
        {allSelected ? t("common:deselectall") : t("common:selectall")}
      </Button>
    </div>
  );
};

export default WeekdayButtons;

interface DayButtonProps {
  t: TFunction;
  day: string;
  isActive: boolean;
  onClick: () => void;
  tabIndex?: number;
  disabled?: boolean;
  ref?: React.Ref<HTMLButtonElement>;
}

const DayButton = ({ t, day, isActive, onClick, tabIndex, disabled, ref }: DayButtonProps): JSX.Element => {
  return (
    <div className="group relative">
      <Button
        ref={ref}
        tabIndex={tabIndex}
        variant={"outline"}
        className={cn(isActive && "bg-slate-600 text-white hover:text-white hover:bg-slate-400")}
        type="button"
        onClick={onClick}
        disabled={disabled}
      >
        {t("common:weekdaysshort." + day)}
      </Button>
      {disabled && day === "holiday" && (
        <p className="bg-white p-2 rounded-md border-[1px] border-slate-200 absolute bottom-12 -left-10
                      group-hover:opacity-100 opacity-0 text-xs text-red-500 w-[200px] z-10 shadow-md"
        >
          {t("translation:shifts.holidayselect")}
        </p>
      )}
    </div>
  );
};
